import React, { useEffect, useState } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';
import { Button } from './ui/Button';
import { getBranchesByCompany } from '../utils/companyManagement';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
interface EmployeeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (employee: any) => void;
  editingEmployee?: any;
}
export function EmployeeFormModal({
  isOpen,
  onClose,
  onSave,
  editingEmployee
}: EmployeeFormModalProps) {
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const {
    addLog
  } = useActivityLog();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    password: '',
    position: '',
    companyId: 0,
    branchId: '',
    status: 'Active',
    permissions: {
      canOrder: true,
      canViewReports: false,
      canManageMenu: false,
      canManageEmployees: false
    }
  });
  const [availableBranches, setAvailableBranches] = useState<any[]>([]);
  // Load companies from Supabase
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setLoading(true);
        if (isSupabaseConfigured()) {
          const data = await supabaseClient.getCompanies();
          setCompanies(data);
          if (data.length > 0 && !editingEmployee) {
            setFormData(prev => ({
              ...prev,
              companyId: data[0].id
            }));
          }
        } else {
          // Fallback to localStorage
          const saved = localStorage.getItem('criket_companies');
          const localCompanies = saved ? JSON.parse(saved) : [];
          setCompanies(localCompanies);
          if (localCompanies.length > 0 && !editingEmployee) {
            setFormData(prev => ({
              ...prev,
              companyId: localCompanies[0].id
            }));
          }
        }
      } catch (error) {
        console.error('Error loading companies:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) {
      loadCompanies();
    }
  }, [isOpen]);
  // Update form when modal opens or editingEmployee changes
  useEffect(() => {
    if (!isOpen || companies.length === 0) return;
    if (editingEmployee) {
      setFormData({
        name: editingEmployee.name || '',
        email: editingEmployee.email || '',
        username: editingEmployee.username || '',
        password: '',
        position: editingEmployee.position || '',
        companyId: editingEmployee.companyId || companies[0]?.id || 0,
        branchId: editingEmployee.branchId || '',
        status: editingEmployee.status || 'Active',
        permissions: editingEmployee.permissions || {
          canOrder: true,
          canViewReports: false,
          canManageMenu: false,
          canManageEmployees: false
        }
      });
    } else {
      // Reset form for new employee
      const branches = getBranchesByCompany(companies[0]?.id || 0);
      setFormData({
        name: '',
        email: '',
        username: '',
        password: '',
        position: '',
        companyId: companies[0]?.id || 0,
        branchId: branches[0]?.id || '',
        status: 'Active',
        permissions: {
          canOrder: true,
          canViewReports: false,
          canManageMenu: false,
          canManageEmployees: false
        }
      });
    }
  }, [isOpen, editingEmployee?.id, companies]);
  useEffect(() => {
    const branches = getBranchesByCompany(formData.companyId);
    setAvailableBranches(branches);
    // Only auto-select first branch if creating new employee and no branch selected
    if (!editingEmployee && branches.length > 0 && !formData.branchId) {
      setFormData(prev => ({
        ...prev,
        branchId: branches[0].id
      }));
    }
  }, [formData.companyId]);
  if (!isOpen) return null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.username) {
      alert('El nombre de usuario es requerido');
      return;
    }
    if (!editingEmployee && !formData.password) {
      alert('La contraseña es requerida para nuevos empleados');
      return;
    }
    const company = companies.find(c => c.id === formData.companyId);
    const branch = availableBranches.find(b => b.id === formData.branchId);
    const employeeData = {
      ...formData,
      company: company?.name || '',
      branch: branch?.name || ''
    };
    onSave(employeeData);
    onClose();
  };
  if (loading) {
    return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-163">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800 p-8" data-id="element-164">
          <div className="text-center" data-id="element-165">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4" data-id="element-166"></div>
            <p className="text-gray-500 dark:text-gray-400" data-id="element-167">
              Cargando empresas...
            </p>
          </div>
        </div>
      </div>;
  }
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-168">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800 max-h-[90vh] overflow-y-auto" data-id="element-169">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800 sticky top-0 bg-white dark:bg-gray-900" data-id="element-170">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-171">
            {editingEmployee ? 'Editar' : 'Nuevo'} Empleado
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-172">
            <X size={20} data-id="element-173" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-id="element-174">
          <div data-id="element-175">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-176">
              Nombre Completo
            </label>
            <input type="text" required value={formData.name} onChange={e => setFormData({
            ...formData,
            name: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Juan Pérez" data-id="element-177" />
          </div>

          <div data-id="element-178">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-179">
              Correo Electrónico
            </label>
            <input type="email" required value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="juan@empresa.com" data-id="element-180" />
          </div>

          <div data-id="element-181">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-182">
              Nombre de Usuario
            </label>
            <input type="text" required value={formData.username} onChange={e => setFormData({
            ...formData,
            username: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="juanperez" data-id="element-183" />
          </div>

          <div data-id="element-184">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-185">
              Contraseña {editingEmployee && '(dejar en blanco para mantener)'}
            </label>
            <div className="relative" data-id="element-186">
              <input type={showPassword ? 'text' : 'password'} required={!editingEmployee} value={formData.password} onChange={e => setFormData({
              ...formData,
              password: e.target.value
            })} className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="••••••••" data-id="element-187" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-188">
                {showPassword ? <EyeOff size={18} data-id="element-189" /> : <Eye size={18} data-id="element-190" />}
              </button>
            </div>
          </div>

          <div data-id="element-191">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-192">
              Cargo/Posición
            </label>
            <input type="text" required value={formData.position} onChange={e => setFormData({
            ...formData,
            position: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Desarrollador" data-id="element-193" />
          </div>

          <div data-id="element-194">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-195">
              Empresa
            </label>
            <select value={formData.companyId} onChange={e => setFormData({
            ...formData,
            companyId: parseInt(e.target.value)
          })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-196">
              {companies.map(company => <option key={company.id} value={company.id} data-id="element-197">
                  {company.name}
                </option>)}
            </select>
          </div>

          <div data-id="element-198">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-199">
              Sucursal ({availableBranches.length} disponibles)
            </label>
            <select value={formData.branchId} onChange={e => setFormData({
            ...formData,
            branchId: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" required data-id="element-200">
              {availableBranches.length === 0 ? <option value="" data-id="element-201">No hay sucursales disponibles</option> : availableBranches.map(branch => <option key={branch.id} value={branch.id} data-id="element-202">
                    {branch.name} - {branch.address}
                  </option>)}
            </select>
          </div>

          <div data-id="element-203">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-204">
              Estado
            </label>
            <select value={formData.status} onChange={e => setFormData({
            ...formData,
            status: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-205">
              <option value="Active" data-id="element-206">Activo</option>
              <option value="Inactive" data-id="element-207">Inactivo</option>
            </select>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-800 pt-4" data-id="element-208">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-209">
              Permisos
            </label>
            <div className="space-y-2" data-id="element-210">
              <label className="flex items-center gap-2 cursor-pointer" data-id="element-211">
                <input type="checkbox" checked={formData.permissions.canOrder} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  canOrder: e.target.checked
                }
              })} className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black" data-id="element-212" />
                <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-213">
                  Puede hacer pedidos
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer" data-id="element-214">
                <input type="checkbox" checked={formData.permissions.canViewReports} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  canViewReports: e.target.checked
                }
              })} className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black" data-id="element-215" />
                <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-216">
                  Puede ver reportes
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer" data-id="element-217">
                <input type="checkbox" checked={formData.permissions.canManageMenu} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  canManageMenu: e.target.checked
                }
              })} className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black" data-id="element-218" />
                <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-219">
                  Puede gestionar menú
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer" data-id="element-220">
                <input type="checkbox" checked={formData.permissions.canManageEmployees} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  canManageEmployees: e.target.checked
                }
              })} className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black" data-id="element-221" />
                <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-222">
                  Puede gestionar empleados
                </span>
              </label>
            </div>
          </div>

          <div className="flex gap-3 pt-4" data-id="element-223">
            <Button type="button" variant="outline" onClick={onClose} fullWidth data-id="element-224">
              Cancelar
            </Button>
            <Button type="submit" fullWidth disabled={availableBranches.length === 0} data-id="element-225">
              {editingEmployee ? 'Actualizar' : 'Guardar'} Empleado
            </Button>
          </div>
        </form>
      </div>
    </div>;
}